import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Star, Calendar } from 'lucide-react';

// Gender-based realistic names
const femaleNames = [
  'Ananya Iyer', 'Riya Mehta', 'Zara Yusuf', 'Sofia Patel', 'Jenifer Jeni',
  'Kavitha Raj', 'Ayesha Malik', 'Lina Arora', 'Divya Sharma', 'Sara Khan'
];
const maleNames = [
  'Rahul Verma', 'Ali Khan', 'Carlos Vega', 'John Carter', 'Mohammed Abbas',
  'Suresh Kumar', 'Arjun Nair', 'David George', 'Ahmed Syed', 'Jason Roy'
];

// Generate dummy users
const dummyUsers = Array.from({ length: 40 }).map((_, i) => {
  const isFemale = i % 2 === 0;
  const name = isFemale
    ? femaleNames[i % femaleNames.length]
    : maleNames[i % maleNames.length];

  return {
    id: i + 1,
    name,
    business: `${name.split(' ')[0]}'s FX Hub`,
    roles: ['Trader', 'Mentor', 'Analyst', 'Coder'].filter((_, idx) => idx % (i % 3 + 1) === 0),
    location: ['India', 'USA', 'UK', 'UAE'][i % 4],
    verified: i % 3 === 0,
    badge: i % 5 === 0 ? 'Top 1%' : 'Rising Star',
    photo: `https://randomuser.me/api/portraits/${isFemale ? 'women' : 'men'}/${i}.jpg`,
    rating: Math.floor(Math.random() * 3) + 3,
    online: i % 2 === 0
  };
});

export default function UserDirectoryBox() {
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  const startIndex = (currentPage - 1) * usersPerPage;
  const currentUsers = dummyUsers.slice(startIndex, startIndex + usersPerPage);
  const totalPages = Math.ceil(dummyUsers.length / usersPerPage);

  const renderStars = (count) => {
    return (
      <div className="flex items-center gap-0.5 mt-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className={i < count ? 'text-yellow-500' : 'text-gray-300'}>★</span>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-6">👥 User Directory</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
        {currentUsers.map((user) => (
          <div
            key={user.id}
            className="border rounded-lg shadow-sm p-4 bg-gray-50 hover:shadow-md transition-all duration-300"
          >
            <img
              src={user.photo}
              alt={user.name}
              className="w-full h-48 object-cover rounded-md mb-3"
            />
            <h3 className="text-lg font-bold">{user.name}</h3>
            <p className="text-sm text-gray-600 italic mb-1">{user.business}</p>

            <div className="text-sm text-gray-700 mb-1">
              <span className="font-semibold">Roles:</span> {user.roles.join(', ')}
            </div>

            {renderStars(user.rating)}

            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  user.online ? 'bg-green-500' : 'bg-red-500'
                }`}
              ></span>
              <span className="text-xs text-gray-600">
                {user.online ? 'Online' : 'Offline'}
              </span>
            </div>

            {user.verified && (
              <p className="text-green-600 text-xs font-medium mt-1">✔ Verified</p>
            )}

            <span className="text-xs mt-2 bg-yellow-100 text-yellow-700 inline-block px-2 py-1 rounded">
              {user.badge}
            </span>

            {/* Action Icons */}
            <div className="flex gap-3 mt-4">
              <button
                title="Follow"
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700"
              >
                <Star size={18} />
              </button>

              <Link
                to={`/dashboard/user/${user.id}`}
                title="View Profile"
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                <User size={18} />
              </Link>

              <button
                title="Book Appointment"
                className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700"
              >
                <Calendar size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 gap-2">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 rounded text-sm ${
              currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
